<template>
  <v-container fluid>
    <v-card class="elevation-2 py-3 px-6 rounded-lg">
      <v-row class="pa-0"> 
        <v-col cols="12" md="3" class="d-flex justify-center">
          <v-select
            :items="periods"
            v-model="selectedPeriod"
            label="Period Summary"
            dense
            hide-details
            outlined
            @change="getHeaderForExcel"
        ></v-select>
        </v-col>
        <v-col cols="12" md="3" class="d-flex justify-start justify-md-start pb-0 pt-0 mt-0 mt-md-4">
          <WeekFilters
            v-if="selectedPeriod == 'Week Summary'"
            :weeksEnd="weeksEnd" 
            :weeks="period" 
            @getDates="getWeeks"
            :disabled="loadedWeek"
          />
          <MonthFilters
            v-else
            :end="dateEnd" 
            :start="dateStart" 
            @getDates="getMonths"
            :disabled="loadedMonth"
          />
        </v-col>
        <v-spacer></v-spacer>
        <LastTimeUpdated :selectedDate="ads == 'bing' ? 'updateBingAds' : 'updateGoogleAds'" class=" mt-0 mt-md-4"/>
      </v-row>
    </v-card>
    <v-card class="elevation-2 py-1 px-0 mt-4 rounded-lg" v-if="selectedPeriod == 'Week Summary'">
      <v-card-title>
        <v-row class="pa-0">                
          <v-col cols="12" class="d-flex justify-end justify-md-end pb-0 pt-0 mt-0">
            <v-spacer></v-spacer>
            <vue-excel-xlsx
              :data="dataWeekForExcel"
              :columns="columnsData"
              :filename="`${ads == 'bing' ? 'Bing' : 'Adwords'} Marketing Report - ${selectedPeriod}`"
              :sheetname="`${period} ${period > 1 ? 'weeks' : 'week'} to ${dateEnd}`"
            >
              <v-btn
                height="39"           
                min-width="39px"        
                :disabled="!dataWeeks.data"
                class="ml-auto mr-5 rounded-lg px-0"                           
                color="white"
              >
                <ExportExcel />
              </v-btn> 
            </vue-excel-xlsx>
            <CustomColumns 
              :currentValue="valueWeek"
              :currentCustomHeader="customHeaderWeek"
              :currentHeaders="headersWeek"
              :settingsName="`${settingsName}Week`"
              @getCustomData="getCustomDataWeek"
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-hover v-model="hoverOnTable">
        <v-card-text>
          <div class="d-flex justify-space-between">        
            <v-btn
              class="mx-2 scroll-btn btn-left-arrow"
              fab
              small     
              @click="scrollingLeft"
            >
              <v-icon>
                mdi-arrow-left
              </v-icon>
            </v-btn>
            <v-btn
              class="mx-2 scroll-btn btn-right-arrow"
              fab
              small
              @click="scrollingRight"
            >
              <v-icon>
                mdi-arrow-right
              </v-icon>
            </v-btn>
          </div>
          <v-data-table
            :headers="selectedHeadersWeek"
            :items="dataWeeks.data"
            item-key="title"
            class="elevation-0 table-main-container"
            fixed-header
            height="538"
            :loading="loadedWeek"
            loading-text="Loading... Please wait"
            :page.sync="pagination.page"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, 75, 100, -1]
              }"
            @click:row="rowClick"
          >
            <template v-slot:item.clicks="{ item }">
              <span v-if="item.clicks > 0 ">{{ item.clicks.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
              <span v-if="item.clicks == 0">0</span>
            </template>
            <template v-slot:item.impressions="{ item }">
              <span v-if="item.impressions > 0 ">{{ item.impressions.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
              <span v-if="item.impressions == 0">0</span>
            </template>
            <template v-slot:item.cost="{ item }">
              <span v-if="item.cost > 0 ">$ {{ item.cost.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
              <span v-if="item.cost == 0">0</span>
            </template>
            <template v-slot:item.cost_per_conversions="{ item }">
              <span v-if="item.cost_per_conversions > 0 ">$ {{ item.cost_per_conversions.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
              <span v-if="item.cost_per_conversions == 0">0</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-hover>    
    </v-card>
    <v-card class="elevation-2 py-1 px-0 mt-4 rounded-lg" v-else>
      <v-card-title>
        <v-row class="pa-0">                
          <v-col cols="12" class="d-flex justify-end justify-md-end pb-0 pt-0 mt-0 mt-md-4">
            <v-spacer></v-spacer>
            <vue-excel-xlsx
              :data="dataMonthForExcel"
              :columns="columnsData"
              :filename="`${ads == 'bing' ? 'Bing' : 'Adwords'} Marketing Report - ${selectedPeriod}`"
              :sheetname="`${dateStart} ~ ${dateEnd}`"
            >
              <v-btn
                height="39"           
                min-width="39px"        
                :disabled="!dataMonths.data"
                class="ml-auto mr-5 rounded-lg px-0"                           
                color="white"
              >
                <ExportExcel />
              </v-btn> 
            </vue-excel-xlsx>
            <CustomColumns 
              :currentValue="valueMonth"
              :currentCustomHeader="customHeaderMonth"
              :currentHeaders="headersMonth"
              :settingsName="`${settingsName}Month`"
              @getCustomData="getCustomDataMonth"
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-hover v-model="hoverOnTable">
        <v-card-text>
          <div class="d-flex justify-space-between">        
            <v-btn
              class="mx-2 scroll-btn btn-left-arrow"
              fab
              small     
              @click="scrollingLeft"
            >
              <v-icon>
                mdi-arrow-left
              </v-icon>
            </v-btn>
            <v-btn
              class="mx-2 scroll-btn btn-right-arrow"
              fab
              small
              @click="scrollingRight"
            >
              <v-icon>
                mdi-arrow-right
              </v-icon>
            </v-btn>
          </div>
          <v-data-table
            :headers="selectedHeadersMonth"
            :items="dataMonths.data"
            :custom-sort="customSort"
            item-key="title"
            class="elevation-0 table-main-container"
            fixed-header
            height="538"
            :loading="loadedMonth"
            loading-text="Loading... Please wait"
            :page.sync="pagination.page"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, 75, 100, -1]
              }"
            @click:row="rowClick"
          >
            <template v-slot:item.clicks="{ item }">
              <span v-if="item.clicks > 0 ">{{ item.clicks.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
              <span v-if="item.clicks == 0">0</span>
            </template>
            <template v-slot:item.impressions="{ item }">
              <span v-if="item.impressions > 0 ">{{ item.impressions.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
              <span v-if="item.impressions == 0">0</span>
            </template>
            <template v-slot:item.cost="{ item }">
              <span v-if="item.cost > 0 ">$ {{ item.cost.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
              <span v-if="item.cost == 0">0</span>
            </template>
            <template v-slot:item.cost_per_conversions="{ item }">
              <span v-if="item.cost_per_conversions > 0 ">$ {{ item.cost_per_conversions.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
              <span v-if="item.cost_per_conversions == 0">0</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-hover>   
    </v-card>
  </v-container>
</template>

<script>
import axios from '@/api/axios-auth'
import MonthFilters from '@/components/filters/MonthFilters'
import WeekFilters from '@/components/filters/WeekFilters'
import CustomColumns from '@/components/customizing/CustomColumns'
import ExportExcel from '@/components/customizing/ExportExcel'
import LastTimeUpdated from "@/views/LastTimeUpdated";

export default {
  components: {
    LastTimeUpdated,
    WeekFilters,
    MonthFilters,
    CustomColumns,
    ExportExcel,
  },
  props: ["ads"],
  data() {
    return {
      periods: ['Week Summary', 'Month Summary'],
      selectedPeriod: 'Week Summary',
      headersWeek: [
          {text: 'Week', value: 'title', width: '112px', class: 'black--text'},
          {text: 'Clicks', value: 'clicks', class: 'black--text'},
          {text: 'Clicks Changes,%', value: 'click_changes_pct', class: 'black--text'},
          {text: 'Impressions', value: 'impressions', class: 'black--text'},
          {text: 'Expenses', value: 'cost', class: 'black--text'},
          {text: 'Expenses Changes,%', value: 'cost_changes_pct', class: 'black--text'},
          {text: 'CPC', value: 'cpc', class: 'black--text'},
          {text: 'Total Conversions', value: 'total_conversions', class: 'black--text'},
          {text: 'Total Conversion changes,%', value: 'total_conversions_changes_pct', class: 'black--text'},
          {text: 'Expenses Per Conversion', value: 'cost_per_conversions', class: 'black--text'},
          {text: 'Expenses Per Conversion Changes,%', value: 'cost_per_conversions_changes_pct', class: 'black--text'},
      ],
      valueWeek: [
          {text: 'Week', value: 'title', width: '112px', class: 'black--text'},
          {text: 'Clicks', value: 'clicks', class: 'black--text'},
          {text: 'Clicks Changes,%', value: 'click_changes_pct', class: 'black--text'},
          {text: 'Impressions', value: 'impressions', class: 'black--text'},
          {text: 'Expenses', value: 'cost', class: 'black--text'},
          {text: 'Expenses Changes,%', value: 'cost_changes_pct', class: 'black--text'},
          {text: 'CPC', value: 'cpc', class: 'black--text'},
          {text: 'Total Conversions', value: 'total_conversions', class: 'black--text'},
          {text: 'Total Conversion changes,%', value: 'total_conversions_changes_pct', class: 'black--text'},
          {text: 'Expenses Per Conversion', value: 'cost_per_conversions', class: 'black--text'},
          {text: 'Expenses Per Conversion Changes,%', value: 'cost_per_conversions_changes_pct', class: 'black--text'},
      ],
      headersByCampaigns: [
          {text: 'Orders Сampaign', value: 'orders_campaign', class: 'black--text'},
          {text: 'Total Gallons Campaign', value: 'total_gallons_campaign', class: 'black--text'},
          {text: 'Total Amt Campaign', value: 'total_amt_campaign', class: 'black--text'},
      ],
      headersMonth: [
          {text: 'Month', value: 'title', width: '136px', class: 'black--text'},
          {text: 'Clicks', value: 'clicks', class: 'black--text'},
          {text: 'Clicks Changes,%', value: 'click_changes_pct', class: 'black--text'},
          {text: 'Impressions', value: 'impressions', class: 'black--text'},
          {text: 'Cost', value: 'cost', class: 'black--text'},
          {text: 'Cost Changes,%', value: 'cost_changes_pct', class: 'black--text'},
          {text: 'CPC', value: 'cpc', class: 'black--text'},
          {text: 'Total Conversions', value: 'total_conversions', class: 'black--text'},
          {text: 'Total Conversion changes,%', value: 'total_conversions_changes_pct', class: 'black--text'},
          {text: 'Cost Per Conversion', value: 'cost_per_conversions', class: 'black--text'},
          {text: 'Cost Per Conversion Changes,%', value: 'cost_per_conversions_changes_pct', class: 'black--text'},
      ],
      valueMonth: [
          {text: 'Month', value: 'title', width: '136px', class: 'black--text'},
          {text: 'Clicks', value: 'clicks', class: 'black--text'},
          {text: 'Clicks Changes,%', value: 'click_changes_pct', class: 'black--text'},
          {text: 'Impressions', value: 'impressions', class: 'black--text'},
          {text: 'Cost', value: 'cost', class: 'black--text'},
          {text: 'Cost Changes,%', value: 'cost_changes_pct', class: 'black--text'},
          {text: 'CPC', value: 'cpc', class: 'black--text'},
          {text: 'Total Conversions', value: 'total_conversions', class: 'black--text'},
          {text: 'Total Conversion changes,%', value: 'total_conversions_changes_pct', class: 'black--text'},
          {text: 'Cost Per Conversion', value: 'cost_per_conversions', class: 'black--text'},
          {text: 'Cost Per Conversion Changes,%', value: 'cost_per_conversions_changes_pct', class: 'black--text'},
      ],
      customHeaderWeek: ['title', 'clicks', 'click_changes_pct', 'impressions', 'cost',
                        'cost_changes_pct', 'cpc', 'total_conversions', 'total_conversions_changes_pct',
                        'cost_per_conversions', 'cost_per_conversions_changes_pct'
                        ],
      customHeaderMonth: ['title', 'clicks', 'click_changes_pct', 'impressions', 'cost',
                        'cost_changes_pct', 'cpc', 'total_conversions', 'total_conversions_changes_pct',
                        'cost_per_conversions', 'cost_per_conversions_changes_pct'
                        ],
      defaultCustomHeader: ['title', 'clicks', 'click_changes_pct', 'impressions', 'cost',
                        'cost_changes_pct', 'cpc', 'total_conversions', 'total_conversions_changes_pct',
                        'cost_per_conversions', 'cost_per_conversions_changes_pct'
                        ],
      selectedHeadersWeek: [],
      selectedHeadersMonth: [],
      dataWeeks: [],
      dataMonths: [],
      dataWeeksByCampaigns: [],
      dataMonthsByCampaigns: [],
      userName: null,
      dateStart: new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 7,
                    new Date(
                        new Date().getFullYear(), 
                        new Date().getMonth()).getDate() + 1
                        ).toISOString().substr(0, 10),
      dateEnd: new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() + 1,
                    ).toISOString().substr(0, 10),
      weeksEnd: new Date().toISOString().substr(0, 10),
      period: '20',
      loadedWeek: false,
      loadedMonth: false,
      options: {
        responsive: true,
        maintainAspectRatio: false
      },
      allCampaigns: [],
      allAdGroups: [],
      campaigns: [],
      dialogCustomColumnsWeek: false,
      dialogCustomColumnsMonth: false,
      settingsName: this.ads,
      columnsData: [],
      scrollLeft: 0,
      scrollWidth: 0,
      clientWidth: 0,
      hoverOnTable: false,
      pagination: {
        page: 1
      }
    }
  },
  watch: {
    ads() {
      this.settingsName = this.ads
      this.pagination.page = 1
      this.getDateRange()
      this.loadReports()
    },
    valueWeek(val) {
      this.selectedHeadersWeek = val
      this.getHeaderForExcel()
    },
    valueMonth(val) {
      this.selectedHeadersMonth = val
      this.getHeaderForExcel()
    },
    hoverOnTable(){
      this.showScrollingArrows()
    },
    selectedPeriod(){
      this.pagination.page = 1
    }
  },
  created() {
    this.selectedHeadersWeek = this.valueWeek
    this.selectedHeadersMonth = this.valueMonth
    this.getHeaderForExcel()
    window.addEventListener("resize", this.showScrollingArrows)
  },
  destroyed() {
    window.removeEventListener("resize", this.showScrollingArrows)
  },
  mounted() {
    this.getDateRange()
    this.settingsName = this.ads

    if (localStorage.getItem('marketingReport')) {
      let data = JSON.parse(localStorage.getItem('marketingReport'))
      this.dateStart = data.dateStart
      this.dateEnd = data.dateEnd
      this.period = data.period
      this.weeksEnd = data.weeksEnd
      this.selectedPeriod = data.selectedPeriod
      localStorage.removeItem('marketingReport')
    }

    axios
        .get('/username')
        .then(response => (this.userName = response.data));
    axios
      .get('/campaigns')
        .then(response => {
          this.allCampaigns = response.data
          for (let i = 0; i < this.allCampaigns.length; i++) {
            this.allAdGroups.push({name: this.allCampaigns[i].campaignName, id: this.allCampaigns[i].campaignId})
          }

          this.campaigns = this._.chain(this.allCampaigns)
              .groupBy("campaignId")
              .map((value, key) => ({id: key, name: value[0].campaignName}))
              .value()
        })

    this.loadReports()
    this.showScrollingArrows()
  },
  computed: {
    dataWeekForExcel(){
      if (!this.dataWeeks.data) return []
      let data = JSON.parse(JSON.stringify(this.dataWeeks.data))

      return data
    },
    dataMonthForExcel(){
      if (!this.dataMonths.data) return []
      let data = JSON.parse(JSON.stringify(this.dataMonths.data))

      return data
    },
  },
  methods: {
    scrollingRight(){
      const el = document.querySelector('.v-data-table__wrapper')

      el.scrollLeft = el.scrollLeft + 50

      const btnRight = document.querySelector('.btn-right-arrow')
      const btnLeft = document.querySelector('.btn-left-arrow')
      btnRight.style.visibility = (this.hoverOnTable && (el.scrollLeft + el.clientWidth != el.scrollWidth)) ? 'visible' : 'hidden'
      btnLeft.style.visibility = this.hoverOnTable ? 'visible' : 'hidden'
    },
    scrollingLeft(){
      const el = document.querySelector('.v-data-table__wrapper')

      el.scrollLeft = el.scrollLeft - 50

      const btnLeft = document.querySelector('.btn-left-arrow')
      const btnRight = document.querySelector('.btn-right-arrow')
      btnLeft.style.visibility = (this.hoverOnTable && (el.scrollLeft != 0)) ? 'visible' : 'hidden'
      btnRight.style.visibility = this.hoverOnTable ? 'visible' : 'hidden'
    },
    showScrollingArrows(){
      const el = document.querySelector('.v-data-table__wrapper')
      this.scrollLeft = el.scrollLeft
      this.scrollWidth = el.scrollWidth
      this.clientWidth = el.clientWidth

      const btnRight = document.querySelector('.btn-right-arrow')
      const btnLeft = document.querySelector('.btn-left-arrow')
      btnRight.style.visibility = (this.hoverOnTable && (this.scrollLeft + this.clientWidth !== this.scrollWidth)) ? 'visible' : 'hidden'
      btnLeft.style.visibility = (this.hoverOnTable && (this.scrollLeft != 0)) ? 'visible' : 'hidden'
    },
    getDateRange(){
      if (sessionStorage.getItem('weeksEnd')) {
        this.weeksEnd = sessionStorage.getItem('weeksEnd')
      }
      if (sessionStorage.getItem('period')) {
        this.period = sessionStorage.getItem('period')
      }
      if (sessionStorage.getItem('monthStart')) {
        this.dateStart = sessionStorage.getItem('monthStart')
      }
      if (sessionStorage.getItem('monthEnd')) {
        this.dateEnd = sessionStorage.getItem('monthEnd')
      }
    },
    getHeaderForExcel(){
      let selectedHeaders = (this.selectedPeriod === 'Week Summary') ? this.selectedHeadersWeek : this.selectedHeadersMonth

      this.columnsData = selectedHeaders.map(e => {
        const obj = {}
        if (e.value !== "actions") {
            obj.label = e.text
            obj.field = e.value
        }
        if (e.value === "cost" || e.value === "cost_per_conversions") {
            obj.label = `${e.text},$`
            obj.field = e.value
        }
        return obj
      })
    },
    customSort: function(items, index, isDesc) {
      items.sort((a, b) => {
          if (index[0]=='title') {
            if (!isDesc[0]) {
                return new Date(b[index]) - new Date(a[index]);
            } else {
                return new Date(a[index]) - new Date(b[index]);
            }
          }
          else {
            if(typeof a[index] !== 'undefined'){
              if (!isDesc[0]) {
                 return a[index] - b[index];
              }
              else {
                  return b[index] - a[index]
              }
            }
          }
      });
      return items;
    },
    loadReports() {
      this.dataWeeks = []
      this.dataMonths = []
      this.loadedWeek = true
      this.loadedMonth = true
      axios
        .get('/reports/marketing/week-summary/' + this.weeksEnd + '/' + this.period + '/' + this.ads)
        .then(response => {
          response.data.data.map(elem => {

            for (let item in elem) {
              if (typeof elem[item]  === 'string' ) {
                continue;
              }
              elem[item] = `${Math.floor(elem[item] * 100) / 100}`
            }
          });
          this.dataWeeks = response.data
          this.loadedWeek = false
        });
    axios
        .get('/reports/marketing/month-summary/' + this.dateStart + '/' + this.dateEnd + '/' + this.ads)
        .then(response => {
          response.data.data.map(elem => {

            for (let item in elem) {
              if (typeof elem[item]  === 'string' ) {
                continue;
              }
              elem[item] = `${Math.floor(elem[item] * 100) / 100}`
            }
          });

          this.dataMonths = response.data
          this.loadedMonth = false
        })

        let userName = localStorage.getItem('username')
        let clientId = (localStorage.getItem('ClientId')) ? localStorage.getItem('ClientId') : 1
        let settings = this.ads
        if (localStorage.getItem(`${userName}.${clientId}.${settings}Week`)) {

          try {
            let valueWeekCash = JSON.parse(localStorage.getItem(`${userName}.${clientId}.${settings}Week`))
            this.valueWeek = valueWeekCash.filter(e => this.headersWeek.find(item => item.value === e.value))
            
            const parsed = JSON.stringify(this.valueWeek)
            localStorage.setItem(`${userName}.${clientId}.${settings}Week`, parsed)

            this.customHeaderWeek = this.valueWeek.map(e => e.value)
          } catch(e) {
            console.log("")
          }
        } else {          
          this.valueWeek = this.headersWeek
          this.customHeaderWeek = this.valueWeek.map(e => e.value)
          this.selectedHeadersWeek = this.headersWeek
        }

          if (localStorage.getItem(`${userName}.${clientId}.${settings}Month`)) {

            try {
              let valueMonthCash = JSON.parse(localStorage.getItem(`${userName}.${clientId}.${settings}Month`))
              this.valueMonth = valueMonthCash.filter(e => this.headersMonth.find(item => item.value === e.value))
              
              const parsed = JSON.stringify(this.valueMonth)
              localStorage.setItem(`${userName}.${clientId}.${settings}Month`, parsed)

              this.customHeaderMonth = this.valueMonth.map(e => e.value)
            } catch(e) {
              console.log("")
            }
          } else {
            this.valueMonth = this.headersMonth
            this.customHeaderMonth = this.valueMonth.map(e => e.value)
            this.selectedHeadersMonth = this.headersMonth
          }
    },

    getWeeks(dates){
        this.period = dates.period
        this.weeksEnd = dates.weeksEnd
        this.pagination.page = 1
        this.applyFiltersWeek()
    },
    getMonths(dates){
        this.dateStart = dates.dateStart
        this.dateEnd = dates.dateEnd
        this.pagination.page = 1
        this.applyFiltersMonth()
    },
    applyFiltersMonth() {
      this.loadedMonth = true
      axios
        .get('/reports/marketing/month-summary/' + this.dateStart + '/' + this.dateEnd + '/' + this.ads)
        .then(response => {
          response.data.data.map(elem => {
            
            for (let item in elem) {
              if (typeof elem[item]  === 'string' ) {
                continue;
              }
              elem[item] = Math.floor(elem[item] * 100) / 100
            }
          });
          this.dataMonths = response.data
          this.loadedMonth = false
        });
    },
    applyFiltersWeek() {
      this.loadedWeek = true
      axios
          .get('/reports/marketing/week-summary/' + this.weeksEnd + '/' + this.period + '/' + this.ads)
          .then(response => {
            response.data.data.map(elem => {
            
            for (let item in elem) {
              if (typeof elem[item]  === 'string' ) {
                continue;
              }
              elem[item] = Math.floor(elem[item] * 100) / 100
            }
          });
            this.dataWeeks = response.data
            this.loadedWeek = false
          });
    },
    getCustomDataWeek({customHeader, value, selectedHeaders}){
      this.customHeaderWeek = customHeader
      this.valueWeek = value
      this.selectedHeadersWeek = selectedHeaders
      this.getHeaderForExcel()
    },
    getCustomDataMonth({customHeader, value, selectedHeaders}){
      this.customHeaderMonth = customHeader
      this.valueMonth = value
      this.selectedHeadersMonth = selectedHeaders
      this.getHeaderForExcel()
    },
    rowClick: function (item, row) {     
      let selectState = (row.isSelected) ? false : true
      row.select(selectState)
    }
  }
}
</script>

<style scoped>
  .btn-right-arrow {
    right: 0px;
    top: 65px;
  }
  .btn-left-arrow {
    left: 0px;
    top: 65px;
  }
</style>